import React, { useRef, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { notify } from '../../../common/functions/react-toastify.function';

const normalizeSiteToken = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toUpperCase().replace(/\s+/g, '');
};

const findSiteColumnIndex = (headers) => {
  const normalized = (h) =>
    String(h || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

  const candidates = new Set(['site_code', 'sitecode', 'site_id', 'siteid']);

  for (let i = 0; i < headers.length; i++) {
    const n = normalized(headers[i]);
    if (candidates.has(n)) {
      return i;
    }
    if (n.includes('site') && (n.includes('code') || n.includes('id'))) {
      return i;
    }
  }
  return -1;
};

const findPoNumberColumnIndex = (headers) => {
  const normalized = (h) =>
    String(h || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
  for (let i = 0; i < headers.length; i++) {
    const n = normalized(headers[i]);
    if (
      n === 'po_number' ||
      n === 'po' ||
      n === 'po_no' ||
      n === 'po_num' ||
      n === 'po#' ||
      n === 'po_number.'
    ) {
      return i;
    }
    if (n.includes('po') && n.includes('number')) {
      return i;
    }
  }
  return -1;
};

/**
 * Parse sites Excel locally. Sites are stored on the invoice only at submit time.
 * If some sites already exist on another invoice for the same PO, those are skipped and reported.
 */
export default function SystemPoSiteExcelUploadSection({
  currentPoNumber,
  existingInvoiceSites,
  onUploaded,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasPoNumber = Boolean(String(currentPoNumber || '').trim());

  const existingNorm = useMemo(() => {
    const set = new Set();
    (existingInvoiceSites || []).forEach((s) => {
      const n = normalizeSiteToken(s);
      if (n) {
        set.add(n);
      }
    });
    return set;
  }, [existingInvoiceSites]);

  const alreadyAddedSites = Array.isArray(result?.alreadyExistsInTable)
    ? result.alreadyExistsInTable
    : [];
  const duplicateSites = Array.isArray(result?.duplicatesInExcel) ? result.duplicatesInExcel : [];
  const insertedCount = Number(result?.inserted ?? 0) || 0;
  const hasUploadDetails =
    insertedCount === 0 ||
    (result?.alreadyExistsInTableCount ?? 0) > 0 ||
    (result?.duplicatesInExcelCount ?? 0) > 0;

  const handlePick = () => {
    if (!hasPoNumber || uploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = (e) => {
    e?.stopPropagation?.();
    const poNumber = String(currentPoNumber || '').trim();
    if (!poNumber) {
      notify('error', 'Please upload a PO first before downloading the sites format.');
      return;
    }
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['PO Number', 'Site Code'],
      [poNumber, ''],
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PO Sites');
    XLSX.writeFile(workbook, `System_PO_Sites_Template_${poNumber}.xlsx`);
  };

  const handleFile = async (file) => {
    if (!file) {
      return;
    }
    const expectedPoNumber = String(currentPoNumber || '').trim();
    if (!expectedPoNumber) {
      notify('error', 'Please upload a PO first before uploading sites.');
      return;
    }

    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      notify('error', 'Please upload a valid Excel/CSV file (.xls, .xlsx, or .csv).');
      return;
    }

    setUploading(true);
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const workbook = XLSX.read(buf, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

      if (!Array.isArray(data) || data.length < 2) {
        notify('error', 'Excel must contain a header row and at least one data row.');
        setUploading(false);
        return;
      }

      const [headers, ...rows] = data;
      const siteColIdx = findSiteColumnIndex(headers || []);
      if (siteColIdx < 0) {
        notify('error', 'Could not find a site column (e.g. Site Code / Site ID).');
        setUploading(false);
        return;
      }
      const poColIdx = findPoNumberColumnIndex(headers || []);
      if (poColIdx < 0) {
        notify('error', 'Could not find a PO number column (e.g. PO Number).');
        setUploading(false);
        return;
      }

      const rawSiteCells = rows
        .map((r) => (Array.isArray(r) ? r[siteColIdx] : undefined))
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '');

      const extractedPoNumbers = rows
        .map((r) => (Array.isArray(r) ? r[poColIdx] : undefined))
        .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
        .filter((v) => v !== '');

      const uniquePo = Array.from(new Set(extractedPoNumbers));
      if (uniquePo.length !== 1) {
        notify('error', 'Excel must contain exactly one PO number for all rows.');
        setUploading(false);
        return;
      }
      const poNumber = uniquePo[0];
      if (poNumber !== expectedPoNumber) {
        notify(
          'error',
          `Uploaded sites belong to PO ${poNumber}. Please upload sites for PO ${expectedPoNumber}.`
        );
        setUploading(false);
        return;
      }

      if (rawSiteCells.length === 0) {
        notify('error', 'No site values found in file.');
        setUploading(false);
        return;
      }

      const byNorm = new Map();
      const normOccurrences = new Map();
      for (const cell of rawSiteCells) {
        const display = String(cell).trim();
        if (!display) {
          continue;
        }
        const n = normalizeSiteToken(display);
        if (!n) {
          continue;
        }
        normOccurrences.set(n, (normOccurrences.get(n) || 0) + 1);
        if (!byNorm.has(n)) {
          byNorm.set(n, display);
        }
      }

      const uniqueSites = Array.from(byNorm.values());
      if (uniqueSites.length === 0) {
        notify('error', 'No valid site values found in file.');
        setUploading(false);
        return;
      }

      const duplicatesInExcel = [...normOccurrences.entries()]
        .filter(([, c]) => c > 1)
        .map(([n]) => byNorm.get(n) || n);

      const conflictsWithExisting = uniqueSites.filter((s) =>
        existingNorm.has(normalizeSiteToken(s))
      );

      const allowedSites = uniqueSites.filter((s) => !existingNorm.has(normalizeSiteToken(s)));

      const res = {
        poNumber,
        inserted: allowedSites.length,
        insertedSites: allowedSites,
        duplicatesInExcel,
        duplicatesInExcelCount: duplicatesInExcel.length,
        alreadyExistsInTable: conflictsWithExisting,
        alreadyExistsInTableCount: conflictsWithExisting.length,
        blocked: allowedSites.length === 0,
      };
      setResult(res);

      if (allowedSites.length > 0 && conflictsWithExisting.length === 0) {
        notify(
          'success',
          `${allowedSites.length} site(s) ready for this invoice (saved when you submit).`
        );
      } else if (allowedSites.length > 0 && conflictsWithExisting.length > 0) {
        notify(
          'error',
          `Skipped ${conflictsWithExisting.length} already-invoiced site(s) for PO ${poNumber}. Added ${allowedSites.length} new site(s).`
        );
      } else {
        const preview = conflictsWithExisting.slice(0, 8).join(', ');
        const extra =
          conflictsWithExisting.length > 8 ? ` (+${conflictsWithExisting.length - 8} more)` : '';
        notify(
          'error',
          `No new sites added. These sites are already used on another invoice for PO ${poNumber}: ${preview}${extra}.`
        );
      }

      if (typeof onUploaded === 'function') {
        onUploaded({
          poNumber: res.poNumber,
          insertedSites: allowedSites,
          alreadyExistsInTable: conflictsWithExisting,
        });
      }

      const hasDetails = duplicatesInExcel.length > 0 || conflictsWithExisting.length > 0;
      if (hasDetails) {
        setDetailsOpen(true);
      }
    } catch (e) {
      notify('error', e?.message || 'Could not read the Excel file.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: '16px', fontFamily: 'Inter', fontWeight: 600, mb: 1 }}>
        Upload Sites Excel
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Sites are checked against other invoices for this PO. They are saved on the invoice when you
        submit (not stored separately).
      </Typography>

      <Box
        onClick={handlePick}
        sx={{
          border: '2px dashed #667687ff',
          borderRadius: '4px',
          padding: '24px',
          textAlign: 'center',
          cursor: !hasPoNumber || uploading ? 'not-allowed' : 'pointer',
          backgroundColor: hasPoNumber ? 'transparent' : 'rgba(0, 0, 0, 0.03)',
          opacity: hasPoNumber ? 1 : 0.55,
          '&:hover': {
            backgroundColor:
              !hasPoNumber || uploading ? 'rgba(0, 0, 0, 0.03)' : 'rgba(25, 118, 210, 0.06)',
          },
        }}
      >
        <AddIcon
          sx={{
            fontSize: '34px',
            color: hasPoNumber ? '#BD1D23' : '#98A2B3',
            mb: 1,
            padding: '5px',
            background: hasPoNumber ? '#EBB9BB' : '#EAECF0',
            borderRadius: '10px',
          }}
        />
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 500, color: '#111' }}>
          {uploading
            ? 'Reading file…'
            : hasPoNumber
              ? 'Click to select Excel/CSV file (.xls, .xlsx, .csv)'
              : 'Upload PO first to enable site upload'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Download format from{' '}
          <span
            style={{
              color: hasPoNumber ? '#BD1D23' : '#98A2B3',
              fontWeight: 600,
              cursor: !hasPoNumber || uploading ? 'not-allowed' : 'pointer',
            }}
            onClick={(e) => {
              if (!hasPoNumber || uploading) {
                return;
              }
              handleDownloadTemplate(e);
            }}
          >
            here
          </span>
        </Typography>
        {uploading && <CircularProgress size={18} sx={{ mt: 1, color: '#BD1D23' }} />}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          // Allow picking the same file again by resetting the input value
          e.target.value = '';
          handleFile(f);
        }}
        disabled={!hasPoNumber || uploading}
      />

      {result && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Sites added for this draft: {insertedCount}
            </Typography>
            {hasUploadDetails && (
              <Button size="small" variant="outlined" onClick={() => setDetailsOpen(true)}>
                View details
              </Button>
            )}
          </Box>
          {insertedCount === 0 && result?.blocked && (
            <Typography variant="body2" sx={{ mt: 1, color: '#BD1D23', fontWeight: 600 }}>
              No sites were added because some rows conflict with existing invoices for this PO.
            </Typography>
          )}
          {insertedCount === 0 && !result?.blocked && (
            <Typography variant="body2" sx={{ mt: 1, color: '#BD1D23', fontWeight: 600 }}>
              Please add sites again, otherwise you will not be able to create the invoice.
            </Typography>
          )}
        </Box>
      )}

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '18px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #BD1D23 0%, #6F1216 100%)',
            color: '#fff',
            py: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Upload Details</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              Duplicate rows in file or sites already invoiced for this PO
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDetailsOpen(false)}
            size="small"
            sx={{ color: '#fff', background: 'rgba(255,255,255,0.12)' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#F8FAFC', p: 2.5 }}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              mt: 2,
              borderRadius: '14px',
              backgroundColor: '#fff',
              border: '1px solid #EAECF0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, color: '#667085', fontWeight: 600 }}>
                PO Number
              </Typography>
              <Typography sx={{ fontSize: 20, color: '#101828', fontWeight: 800 }}>
                {result?.poNumber || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`Sites in draft ${insertedCount}`}
                sx={{
                  backgroundColor: insertedCount > 0 ? '#E8F5E9' : '#FCE4EC',
                  color: insertedCount > 0 ? '#1B5E20' : '#880E4F',
                  fontWeight: 700,
                }}
              />
              <Chip
                size="small"
                label={`Duplicates in file ${result?.duplicatesInExcelCount ?? 0}`}
                sx={{ backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`Already invoiced (this PO) ${result?.alreadyExistsInTableCount ?? 0}`}
                sx={{ backgroundColor: '#E3F2FD', color: '#0D47A1', fontWeight: 700 }}
              />
            </Box>
          </Box>

          {result?.blocked && (result?.alreadyExistsInTableCount ?? 0) > 0 && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '14px',
                backgroundColor: '#FFF5F5',
                border: '1px solid #F5C2C7',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#BD1D23', mb: 0.5 }}>
                Sites already on another invoice for this PO
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#667085' }}>
                Remove these sites from your file or use a different PO. A site can only be invoiced
                once per PO.
              </Typography>
            </Box>
          )}

          {insertedCount === 0 && !result?.blocked && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '14px',
                backgroundColor: '#FFF5F5',
                border: '1px solid #F5C2C7',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#BD1D23', mb: 0.5 }}>
                No sites were added
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#667085' }}>
                Please fix the file and try again.
              </Typography>
            </Box>
          )}

          {(result?.alreadyExistsInTableCount ?? 0) > 0 && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '14px',
                backgroundColor: '#fff',
                border: '1px solid #D6E9FF',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#0D47A1', mb: 1 }}>
                Already invoiced for this PO ({result.alreadyExistsInTableCount})
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {alreadyAddedSites.length > 0
                  ? alreadyAddedSites.map((site) => (
                      <Chip
                        key={site}
                        size="small"
                        label={site}
                        sx={{ backgroundColor: '#E3F2FD', color: '#0D47A1', fontWeight: 700 }}
                      />
                    ))
                  : '—'}
              </Box>
            </Box>
          )}

          {(result?.duplicatesInExcelCount ?? 0) > 0 && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '14px',
                backgroundColor: '#fff',
                border: '1px solid #FFE0B2',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#E65100', mb: 1 }}>
                Duplicate site rows in file ({result.duplicatesInExcelCount})
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#667085', mb: 1 }}>
                Extra duplicate rows were ignored; each site is included once in your draft.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {duplicateSites.length > 0
                  ? duplicateSites.map((site) => (
                      <Chip
                        key={site}
                        size="small"
                        label={site}
                        sx={{ backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 700 }}
                      />
                    ))
                  : '—'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, backgroundColor: '#fff' }}>
          <Button
            onClick={() => setDetailsOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: '#BD1D23',
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#9F171D' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
